import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import * as Cesium from 'cesium'

/**
 * 지도에서 다각형을 그려 변화탐지 분석 영역(WKT, 경위도 POLYGON)으로 쓴다.
 * 좌클릭: 꼭짓점 추가 / 우클릭: 완료(3점 이상) / Esc: 취소.
 * 훅이 언마운트되면(변화탐지 탭 이탈 등) 그린 영역도 지도에서 걷어낸다.
 */
export function useCdAreaDraw(
  viewerRef: RefObject<Cesium.Viewer | null>,
  enabled: boolean,
  defaultCssColor = '#06b6d4',
) {
  const [drawing, setDrawing] = useState(false)
  const [wkt, setWkt] = useState<string | null>(null)

  const pointsRef = useRef<Cesium.Cartesian3[]>([])
  const cursorRef = useRef<Cesium.Cartesian3 | null>(null)
  const entityRef = useRef<Cesium.Entity | null>(null)
  const handlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null)
  const keyHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null)

  const removeEntity = useCallback(() => {
    const v = viewerRef.current
    if (entityRef.current && v && !v.isDestroyed()) v.entities.remove(entityRef.current)
    entityRef.current = null
  }, [viewerRef])

  const stopListening = useCallback(() => {
    handlerRef.current?.destroy()
    handlerRef.current = null
    if (keyHandlerRef.current) window.removeEventListener('keydown', keyHandlerRef.current)
    keyHandlerRef.current = null
    const v = viewerRef.current
    if (v && !v.isDestroyed()) v.canvas.style.cursor = ''
  }, [viewerRef])

  const clear = useCallback(() => {
    stopListening()
    removeEntity()
    pointsRef.current = []
    cursorRef.current = null
    setDrawing(false)
    setWkt(null)
  }, [removeEntity, stopListening])

  /** 그리기 시작. cssColor 로 영역 색을 바꿀 수 있다(예: 제외 영역은 빨강). */
  const start = useCallback((cssColor?: string) => {
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return
    clear()
    const color = Cesium.Color.fromCssColorString(cssColor ?? defaultCssColor)

    const pick = (pos: Cesium.Cartesian2): Cesium.Cartesian3 | undefined => {
      const scene = v.scene
      const picked = scene.pickPositionSupported ? scene.pickPosition(pos) : undefined
      return picked ?? v.camera.pickEllipsoid(pos, scene.globe.ellipsoid) ?? undefined
    }

    entityRef.current = v.entities.add({
      polygon: {
        show: new Cesium.CallbackProperty(
          () => pointsRef.current.length + (cursorRef.current ? 1 : 0) >= 3,
          false,
        ),
        hierarchy: new Cesium.CallbackProperty(() => {
          const pts = cursorRef.current ? [...pointsRef.current, cursorRef.current] : pointsRef.current
          return new Cesium.PolygonHierarchy(pts)
        }, false),
        material: color.withAlpha(0.25),
        perPositionHeight: true,
      },
      polyline: {
        show: new Cesium.CallbackProperty(
          () => pointsRef.current.length + (cursorRef.current ? 1 : 0) >= 2,
          false,
        ),
        positions: new Cesium.CallbackProperty(() => {
          const pts = cursorRef.current ? [...pointsRef.current, cursorRef.current] : pointsRef.current
          return pts.length >= 2 ? [...pts, pts[0]] : pts
        }, false),
        width: 2,
        material: color,
        depthFailMaterial: color,
      },
    })

    const finish = () => {
      const pts = pointsRef.current
      if (pts.length < 3) return
      const ring = pts.map((p) => {
        const c = Cesium.Cartographic.fromCartesian(p)
        return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude)]
      })
      ring.push(ring[0])
      setWkt(`POLYGON((${ring.map(([lon, lat]) => `${lon.toFixed(7)} ${lat.toFixed(7)}`).join(', ')}))`)
      cursorRef.current = null
      stopListening()
      setDrawing(false)
    }

    const handler = new Cesium.ScreenSpaceEventHandler(v.canvas)
    handler.setInputAction((e: { position: Cesium.Cartesian2 }) => {
      const p = pick(e.position)
      if (p) pointsRef.current = [...pointsRef.current, p]
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
    handler.setInputAction((e: { endPosition: Cesium.Cartesian2 }) => {
      cursorRef.current = pick(e.endPosition) ?? null
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)
    handler.setInputAction(finish, Cesium.ScreenSpaceEventType.RIGHT_CLICK)
    handlerRef.current = handler

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clear()
    }
    keyHandlerRef.current = onKey
    window.addEventListener('keydown', onKey)

    v.canvas.style.cursor = 'crosshair'
    setDrawing(true)
  }, [viewerRef, clear, stopListening, defaultCssColor])

  // 탭을 떠나거나 언마운트되면 그린 영역을 정리한다.
  useEffect(() => {
    if (!enabled) clear()
    return () => clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  return { drawing, wkt, start, clear }
}
